<script setup lang="ts">
import api from "@/api";

import { useRoute, useRouter } from "vue-router";

import ReportsTable from "./ReportsTable.vue";
import type { TDriverReport } from "./reportsTypes";

definePage({
	name: "ReportsPage",
	meta: {
		requiresLogin: true,
	},
});

const pageLimit = 12;

const route = useRoute();
const router = useRouter();

const reports = ref<TDriverReport[]>([]);
const count = ref(0);

onBeforeRouteLeave((to) => {
	if (to.query.page !== undefined) {
		const { page, ...restQuery } = to.query;
		return { path: to.path, query: restQuery };
	}
});

const loading = ref(false);

async function postSeen() {
	const ids: number[] = [];

	for (const report of reports.value) {
		if (report.rav_seen_by_admin === null) {
			ids.push(report.rav_id);
		}
	}

	if (ids.length === 0) {
		return;
	}

	try {
		await api.postSeen({
			rav_id: ids,
		});

		for (const report of reports.value) {
			if (ids.includes(report.rav_id)) {
				report.rav_seen_by_admin = 1;
			}
		}
	} catch (error) {
		console.error("Post seen error:", error);
	}
}

function schedulePostSeen() {
	if (seenTimeout) {
		clearTimeout(seenTimeout);
	}

	seenTimeout = setTimeout(() => {
		void postSeen();
	}, 1500);
}

const pageCount = computed(() => {
	return Math.ceil(count.value / pageLimit);
});

const currentPage = computed(() => {
	return Number(route.query.page) || 1;
});

const filteredReports = computed(() => {
	return reports.value;
});

async function getReports(limit = pageLimit, offset = 0) {
	try {
		loading.value = true;

		const res = await api.getReports({
			limit,
			offset,
		});

		const responseData = res.data.data;
		reports.value = responseData.data || [];
		count.value = Number(responseData.meta_info?.[0]?.count || 0);
	} catch (error) {
		console.log(error);

		reports.value = [];
		count.value = 0;
	} finally {
		loading.value = false;
	}
}

async function handlePageChange(pageNumber: number) {
	const offset = pageLimit * (pageNumber - 1);

	await router.push({
		name: "ReportsPage",
		query: {
			page: pageNumber,
		},
	});

	await getReports(pageLimit, offset);
	schedulePostSeen();
}

function openDriver(driverId: number) {
	router.push(`/taxi/${driverId}`);
}

let seenTimeout: ReturnType<typeof setTimeout> | null = null;

onMounted(async () => {
	const offset = pageLimit * (currentPage.value - 1);

	await getReports(pageLimit, offset);
	schedulePostSeen();
});

onBeforeUnmount(() => {
	if (seenTimeout) {
		clearTimeout(seenTimeout);
	}
});
</script>

<template>
	<div id="reports-view">
		<div class="content">
			<div
				v-if="loading"
				class="state-message"
			>
				Učitavanje...
			</div>

			<reports-table
				v-else-if="filteredReports.length > 0"
				:reports="filteredReports"
				@open-driver="openDriver"
			/>

			<div
				v-else
				class="state-message"
			>
				<h1>
					{{ $t("app.labels.noresult") }}
				</h1>
			</div>

			<div
				v-if="!loading && pageCount > 1"
				class="pagination-wrap"
			>
				<pagination-comp
					:total-pages="pageCount"
					:current-page="currentPage"
					@page-changed="handlePageChange"
				/>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
#reports-view {
	width: 100%;
}

.content {
	width: 100%;
	padding: 30px 80px;
}

.control-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
	width: 100%;
	margin-bottom: 20px;
	padding: 0 15px;

	h1 {
		margin: 0;
		font-family: Archivo, sans-serif;
		font-size: 24px;
		font-weight: 600;
	}
}

.search-wrap {
	flex: 0 0 337px;
	width: 337px;
}

.state-message {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 200px;

	h1 {
		font-size: 20px;
		font-weight: 500;
	}
}

.pagination-wrap {
	display: flex;
	justify-content: center;
	width: fit-content;
	margin: 20px auto 0;
}

@media screen and (width <= 768px) {
	.content {
		padding: 15px;
	}

	.control-bar {
		flex-direction: column;
		align-items: stretch;
		padding: 0;
	}

	.search-wrap {
		flex: none;
		width: 100%;
	}
}
</style>
